import { DEPTH_COLORS } from '../Cmarkdownmindmap/mindmap-constants';
import { FlowchartEdgeKind, FlowchartNodeShape } from './parse-mermaid-flowchart';

export enum FlowchartIcon {
  Question = 'question',
  Database = 'database',
  Document = 'document',
  IO = 'io',
  Gear = 'gear',
}

export interface FlowchartNodeSizeAdjustment {
  minWidth: number;
  minHeight: number;
  extraWidth: number;
  extraHeight: number;
  square: boolean;
  maxSquareSize: number | null;
}

export const FLOWCHART_COLORS = [
  DEPTH_COLORS[0],
  DEPTH_COLORS[1],
  DEPTH_COLORS[2],
  DEPTH_COLORS[3],
  DEPTH_COLORS[4],
  DEPTH_COLORS[5],
  DEPTH_COLORS[6],
  '#93D8FF',
  '#CBD5E1',
  '#8B9BB4',
];

export const NODE_FILL = '#131823';
export const NODE_TEXT = 'rgba(255, 255, 255, 0.85)';
export const EDGE_LABEL_BACKGROUND = '#0F131D';
export const EDGE_LABEL_TEXT = '#94A3B8';
export const DIMMED_OPACITY = 0.25;
export const TRANSITION_DURATION = '150ms';

/**
 * Neutral hairline border for a plain process node: a faint cool-white line so a
 * default rectangle reads as a calm dark card rather than a colored accent chip.
 * Used as the resting silhouette color (the ring renders the accent near full, so
 * this value IS the line you see). Tinted, never pure white.
 */
export const NODE_HAIRLINE = 'rgba(255, 255, 255, 0.13)';

export const NODE_TEXT_CHAR_WIDTH = 7.2;
export const NODE_MIN_WIDTH = 108;
export const NODE_MAX_WIDTH = 280;
export const NODE_BASE_HEIGHT = 52;
export const NODE_HORIZONTAL_PADDING = 26;
export const GROUP_LABEL_HEIGHT = 34;
export const GROUP_NODE_MIN_WIDTH = 180;
export const GROUP_NODE_MIN_HEIGHT = 118;
export const GROUP_NODE_PADDING_X = 36;
export const GROUP_NODE_PADDING_Y = 30;
export const DAGRE_NODESEP = 48;
export const DAGRE_RANKSEP = 72;
export const DAGRE_EDGESEP = 20;
export const DAGRE_MARGIN_X = 18;
export const DAGRE_MARGIN_Y = 18;
export const SELF_LOOP_RADIUS = 42;
export const PARALLEL_EDGE_GAP = 18;

export const SHAPE_SIZE_ADJUSTMENT: Record<FlowchartNodeShape, FlowchartNodeSizeAdjustment> = {
  [FlowchartNodeShape.Terminator]: {
    minWidth: 122,
    minHeight: NODE_BASE_HEIGHT,
    extraWidth: 14,
    extraHeight: 0,
    square: false,
    maxSquareSize: null,
  },
  [FlowchartNodeShape.Process]: {
    minWidth: NODE_MIN_WIDTH,
    minHeight: NODE_BASE_HEIGHT,
    extraWidth: 0,
    extraHeight: 0,
    square: false,
    maxSquareSize: null,
  },
  [FlowchartNodeShape.Decision]: {
    // The silhouette is the canonical inscribed diamond (vertices at the edge
    // midpoints), so the node's width:height ratio sets the left/right vertex
    // angle. A wide+short box made that angle very acute (off-brand "pointy").
    // A near-4:3 box (e.g. 124x96 for a short label) opens it to a balanced
    // diamond while still comfortably seating a two-line short label.
    minWidth: 124,
    minHeight: NODE_BASE_HEIGHT + 44,
    extraWidth: 18,
    extraHeight: 44,
    square: false,
    maxSquareSize: null,
  },
  [FlowchartNodeShape.InputOutput]: {
    minWidth: 138,
    minHeight: NODE_BASE_HEIGHT,
    extraWidth: 28,
    extraHeight: 0,
    square: false,
    maxSquareSize: null,
  },
  [FlowchartNodeShape.Connector]: {
    minWidth: 72,
    minHeight: 72,
    extraWidth: 0,
    extraHeight: 0,
    square: true,
    maxSquareSize: 180,
  },
  [FlowchartNodeShape.Document]: {
    minWidth: NODE_MIN_WIDTH,
    minHeight: NODE_BASE_HEIGHT + 12,
    extraWidth: 0,
    extraHeight: 12,
    square: false,
    maxSquareSize: null,
  },
  [FlowchartNodeShape.Database]: {
    minWidth: NODE_MIN_WIDTH,
    minHeight: NODE_BASE_HEIGHT + 16,
    extraWidth: 0,
    extraHeight: 16,
    square: false,
    maxSquareSize: null,
  },
  [FlowchartNodeShape.PredefinedProcess]: {
    minWidth: 126,
    minHeight: NODE_BASE_HEIGHT,
    extraWidth: 18,
    extraHeight: 0,
    square: false,
    maxSquareSize: null,
  },
  [FlowchartNodeShape.Hexagon]: {
    minWidth: 132,
    minHeight: NODE_BASE_HEIGHT + 8,
    extraWidth: 24,
    extraHeight: 8,
    square: false,
    maxSquareSize: null,
  },
  [FlowchartNodeShape.Trapezoid]: {
    minWidth: 138,
    minHeight: NODE_BASE_HEIGHT,
    extraWidth: 28,
    extraHeight: 0,
    square: false,
    maxSquareSize: null,
  },
};

export const SHAPE_ICON: Record<FlowchartNodeShape, FlowchartIcon | null> = {
  [FlowchartNodeShape.Terminator]: null,
  [FlowchartNodeShape.Process]: null,
  // No icon inside the diamond — the "?" stole space from short labels and the
  // diamond silhouette already reads as a decision. (Other shapes keep theirs.)
  [FlowchartNodeShape.Decision]: null,
  [FlowchartNodeShape.InputOutput]: FlowchartIcon.IO,
  [FlowchartNodeShape.Connector]: null,
  [FlowchartNodeShape.Document]: FlowchartIcon.Document,
  [FlowchartNodeShape.Database]: FlowchartIcon.Database,
  [FlowchartNodeShape.PredefinedProcess]: FlowchartIcon.Gear,
  [FlowchartNodeShape.Hexagon]: null,
  [FlowchartNodeShape.Trapezoid]: null,
};

/**
 * Resting silhouette color per shape. The reference is restrained: dark cards
 * with one green (flow / terminator / connector / hexagon), one cool blue
 * (decision and the data-bearing input-output) and a neutral hairline for the
 * plain card-like shapes (process / document / database / predefined / trapezoid)
 * so the diagram never goes rainbow. `DEPTH_COLORS[0]`/`[1]` are the shared green
 * and blue reused from the mindmap. A role override (Start green / End maroon)
 * supersedes this per terminator in {@link resolveFlowchartRoleColors}.
 */
export const DEFAULT_ACCENT_BY_SHAPE: Record<FlowchartNodeShape, string> = {
  [FlowchartNodeShape.Terminator]: DEPTH_COLORS[0],
  [FlowchartNodeShape.Process]: NODE_HAIRLINE,
  [FlowchartNodeShape.Decision]: DEPTH_COLORS[1],
  [FlowchartNodeShape.InputOutput]: DEPTH_COLORS[1],
  [FlowchartNodeShape.Connector]: DEPTH_COLORS[0],
  [FlowchartNodeShape.Document]: NODE_HAIRLINE,
  [FlowchartNodeShape.Database]: NODE_HAIRLINE,
  [FlowchartNodeShape.PredefinedProcess]: NODE_HAIRLINE,
  [FlowchartNodeShape.Hexagon]: DEPTH_COLORS[0],
  [FlowchartNodeShape.Trapezoid]: NODE_HAIRLINE,
};

export const EDGE_WEIGHT_BY_KIND: Record<FlowchartEdgeKind, number> = {
  [FlowchartEdgeKind.Arrow]: 1,
  [FlowchartEdgeKind.Open]: 1,
  [FlowchartEdgeKind.Dotted]: 1,
  [FlowchartEdgeKind.Thick]: 2,
};
